import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, CheckCircle, XCircle, Clock, Trash2 } from 'lucide-react';
import Layout from '@/components/Layout';
import { createTranslator } from '@/i18n';
import { useLanguage } from '@/context/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LeaveRequests({ user, onLogout }) {
  const { language } = useLanguage();
  const t = createTranslator(language || 'is');
  const [requests, setRequests] = useState([]);
  const [open, setOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [editOpen, setEditOpen] = useState(false);
  const [editingRequest, setEditingRequest] = useState(null);
  const [editForm, setEditForm] = useState({
    leave_type: 'vacation',
    start_date: '',
    end_date: '',
    reason: '',
    status: 'pending',
  });

  const [formData, setFormData] = useState({
    leave_type: 'vacation',
    start_date: '',
    end_date: '',
    reason: '',
    user_id: '',
  });
  const [employees, setEmployees] = useState([]);
  const fetchEmployees = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/employees`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEmployees(response.data || []);
    } catch (error) {
      console.error('Villa við að sækja starfsmenn:', error);
    }
  }, []);

  const fetchRequests = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/leave/requests`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRequests(response.data);
    } catch (error) {
      toast.error(t('leave_fetch_error'));
    }
  }, []);

  useEffect(() => {
    fetchRequests();
    if (user?.role === 'admin') {
      fetchEmployees();
    }
  }, [fetchRequests, fetchEmployees, user]);

  // Prefill frá admin dashboard: /leave?new=1&user_id=...&date=...
  useEffect(() => {
    if (searchParams.get('new') === '1') {
      const d = searchParams.get('date') || new Date().toISOString().slice(0, 10);
      setFormData((f) => ({
        ...f,
        user_id: searchParams.get('user_id') || '',
        start_date: d,
        end_date: d,
        leave_type: 'sick',
      }));
      setOpen(true);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/leave/request`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(t('leave_request_sent'));
      setOpen(false);
      fetchRequests();
      setFormData({
        leave_type: 'vacation',
        start_date: '',
        end_date: '',
        reason: '',
        user_id: '',
      });
    } catch (error) {
      console.error('Submit error:', error);
      const errorMsg = error.response?.data?.detail;
      if (typeof errorMsg === 'string') {
        toast.error(errorMsg);
      } else {
        toast.error(t('leave_generic_error'));
      }
    }
  };

  const handleApproval = async (requestId, status) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/leave/approve`,
        { request_id: requestId, status },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(status === 'approved' ? t('leave_approved') : t('leave_rejected'));
      fetchRequests();
    } catch (error) {
      toast.error(t('leave_generic_error'));
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'rejected':
        return <XCircle className="h-5 w-5 text-red-500" />;
      default:
        return <Clock className="h-5 w-5 text-yellow-500" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'approved':
        return 'Samþykkt';
      case 'rejected':
        return 'Hafnað';
      default:
        return 'Í bið';
    }
  };

  const openEditDialog = (request) => {
    setEditingRequest(request);
    setEditForm({
      leave_type: request.leave_type || 'vacation',
      start_date: request.start_date || '',
      end_date: request.end_date || '',
      reason: request.reason || '',
      status: request.status || 'pending',
    });
    setEditOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editingRequest) return;

    try {
      const token = localStorage.getItem('token');
      const payload = {
        leave_type: editForm.leave_type,
        start_date: editForm.start_date,
        end_date: editForm.end_date,
        reason: editForm.reason,
      };

      // Aðeins admin má breyta stöðu
      if (user.role === 'admin') {
        payload.status = editForm.status;
      }

      await axios.put(`${API}/leave/requests/${editingRequest.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success(t('leave_request_updated'));
      setEditOpen(false);
      setEditingRequest(null);
      fetchRequests();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Tókst ekki að uppfæra beiðni';
      toast.error(msg);
    }
  };

  const handleDelete = async (request) => {
    if (!window.confirm('Ertu viss um að vilja eyða þessari beiðni?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/leave/requests/${request.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(t('leave_request_deleted'));
      fetchRequests();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Tókst ekki að eyða beiðni';
      toast.error(msg);
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-8" data-testid="leave-requests-page">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold mb-1">{t('leave_title')}</h1>
            <p className="text-gray-600">{t('leave_subtitle')}</p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button
                className="bg-gradient-to-r from-blue-500 to-purple-600"
                data-testid="new-leave-request-button"
              >
                <Plus className="mr-2 h-4 w-4" />
                {t('leave_new_request')}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{t('leave_dialog_title')}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                {user.role === 'admin' && (
                  <div className="space-y-2">
                    <Label>{t('leave_employee_label')}</Label>
                    <select
                      value={formData.user_id}
                      onChange={(e) => setFormData({ ...formData, user_id: e.target.value })}
                      className="w-full px-3 py-2 border rounded-md"
                      data-testid="leave-employee-select"
                    >
                      <option value="">
                        {t('leave_employee_select_placeholder')}
                      </option>
                      {employees.map((emp) => (
                        <option key={emp.user_id} value={emp.user_id}>
                          {emp.full_name} ({emp.email})
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="space-y-2">
                  <Label>{t('leave_type_label')}</Label>
                  <select
                    value={formData.leave_type}
                    onChange={(e) => setFormData({ ...formData, leave_type: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md"
                    data-testid="leave-type-select"
                  >
                    <option value="vacation">
                      {t('leave_type_option_vacation')}
                    </option>
                    <option value="sick">{t('leave_type_option_sick')}</option>
                    <option value="unpaid_absence">{t('leave_type_option_unpaid')}</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>{t('leave_from_label')}</Label>
                  <Input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) =>
                      setFormData({ ...formData, start_date: e.target.value })
                    }
                    required
                    data-testid="start-date-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('leave_to_label')}</Label>
                  <Input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) =>
                      setFormData({ ...formData, end_date: e.target.value })
                    }
                    required
                    data-testid="end-date-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('leave_reason_label')}</Label>
                  <Textarea
                    value={formData.reason}
                    onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                    required
                    data-testid="reason-textarea"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full"
                  data-testid="submit-leave-request-button"
                >
                  {t('leave_submit_button')}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid gap-4">
          {requests.map((request) => (
            <Card key={request.id} className="shadow-md">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>
                    {request.leave_type === 'vacation'
                      ? t('leave_type_option_vacation')
                      : request.leave_type === 'unpaid_absence'
                        ? t('leave_type_option_unpaid')
                        : t('leave_type_option_sick')}
                    {request.leave_type === 'sick' && Array.isArray(request.paid_dates) && (
                      <span
                        className="ml-2 text-xs font-normal text-gray-500"
                        data-testid={`sick-split-${request.id}`}
                      >
                        {request.paid_dates.length} greiddir
                        {(request.unpaid_dates || []).length > 0 && (
                          <span className="text-amber-600"> · {request.unpaid_dates.length} ógreiddir</span>
                        )}
                      </span>
                    )}
                  </span>
                  <div className="flex items-center gap-2">
                    {getStatusIcon(request.status)}
                    <span className="text-sm font-normal">
                      {getStatusText(request.status)}
                    </span>

                    {/* Breyta-beiðni modal */}
                    <Dialog open={editOpen} onOpenChange={setEditOpen}>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>{t('btn_edit')} beiðni</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleEditSubmit} className="space-y-4">
                          <div className="space-y-2">
                            <Label>{t('leave_employee_label')}</Label>
                            <Input
                              value={editingRequest?.employee_name || ''}
                              disabled
                              className="bg-gray-100"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>{t('leave_type_label')}</Label>
                            <select
                              value={editForm.leave_type}
                              onChange={(e) =>
                                setEditForm({ ...editForm, leave_type: e.target.value })
                              }
                              className="w-full px-3 py-2 border rounded-md"
                            >
                              <option value="vacation">
                                {t('leave_type_option_vacation')}
                              </option>
                              <option value="sick">
                                {t('leave_type_option_sick')}
                              </option>
                              <option value="unpaid_absence">
                                {t('leave_type_option_unpaid')}
                              </option>
                            </select>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label>{t('leave_from_label')}</Label>
                              <Input
                                type="date"
                                value={editForm.start_date}
                                onChange={(e) =>
                                  setEditForm({
                                    ...editForm,
                                    start_date: e.target.value,
                                  })
                                }
                                required
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>{t('leave_to_label')}</Label>
                              <Input
                                type="date"
                                value={editForm.end_date}
                                onChange={(e) =>
                                  setEditForm({
                                    ...editForm,
                                    end_date: e.target.value,
                                  })
                                }
                                required
                              />
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label>{t('leave_reason_label')}</Label>
                            <Textarea
                              value={editForm.reason}
                              onChange={(e) =>
                                setEditForm({ ...editForm, reason: e.target.value })
                              }
                              required
                            />
                          </div>
                          {user.role === 'admin' && (
                            <div className="space-y-2">
                              <Label>{t('leave_status_label')}</Label>
                              <Select
                                value={editForm.status}
                                onValueChange={(val) =>
                                  setEditForm({ ...editForm, status: val })
                                }
                              >
                                <SelectTrigger className="w-full">
                                  <SelectValue placeholder="Veldu stöðu" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="pending">
                                    {t('timesheet_status_pending')}
                                  </SelectItem>
                                  <SelectItem value="approved">
                                    {t('timesheet_status_approved')}
                                  </SelectItem>
                                  <SelectItem value="rejected">
                                    {t('timesheet_status_rejected')}
                                  </SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          )}
                          <Button type="submit" className="w-full">
                            {t('btn_save')}
                          </Button>
                        </form>
                      </DialogContent>
                    </Dialog>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div>
                    <p className="text-sm text-gray-500">
                      {t('leave_employee_label')}
                    </p>
                    <p className="font-medium">
                      {request.employee_name || 'Óþekktur starfsmaður'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Tímabil</p>
                    <p className="font-medium">
                      {new Date(request.start_date).toLocaleDateString('is-IS')} -{' '}
                      {new Date(request.end_date).toLocaleDateString('is-IS')}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Ástæða</p>
                    <p className="font-medium">{request.reason}</p>
                  </div>
                  <div className="flex gap-2 mt-4">
                    {(user.role === 'admin' ||
                      (user.id === request.user_id && request.status === 'pending')) && (
                      <Button variant="outline" onClick={() => openEditDialog(request)}>
                        {t('btn_edit')}
                      </Button>
                    )}
                    {user.role === 'admin' && request.status === 'pending' && (
                      <>
                        <Button
                          onClick={() => handleApproval(request.id, 'approved')}
                          className="bg-green-500 hover:bg-green-600"
                          data-testid={`approve-request-${request.id}`}
                        >
                          {t('leave_approved')}
                        </Button>
                        <Button
                          onClick={() => handleApproval(request.id, 'rejected')}
                          className="bg-red-500 hover:bg-red-600"
                          data-testid={`reject-request-${request.id}`}
                        >
                          {t('leave_rejected')}
                        </Button>
                      </>
                    )}
                    <Button
                      variant="outline"
                      size="icon"
                      className="ml-auto"
                      onClick={() => handleDelete(request)}
                      data-testid={`delete-request-${request.id}`}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          {requests.length === 0 && (
            <Card>
              <CardContent className="py-12 text-center text-gray-500">
                Engar beiðnir fundust
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </Layout>
  );
}
